import { useState, useEffect, useRef, useMemo } from 'preact/hooks'
import { Plus, Calendar, Mail, Phone, MessageSquare, Pencil, Trash2, AlertCircle, Loader2, ChevronDown } from 'lucide-preact'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import type { TouchPointViewDto } from '@/types/touchPoint'
import type { LookupOptionDto } from '@/types/lookups'
import { TouchPointType, EmailEventType, EmailOffsetUnit } from '@/types/touchPoint'
import { formatEmailRule } from '@/utils/emailRuleFormatter'
import { parseServerTimestamp } from '@/utils/time'
import { TouchPointModal, type TouchPointFormData } from '../modals/TouchPointModal'
import { EmailActivationPanel } from '../modals/EmailActivationPanel'
import { ConfirmationModal } from '@/components/modals/ConfirmationModal'
import { useLookups } from '@/hooks/useLookups'
import { useTouchPointManager } from '@/hooks/useTouchPointManager'
import { buildTouchPointCreateDto, buildTouchPointUpdateDto } from '@/utils/touchPointDtos'
import type { WebViewDto } from '@/api/webs'
import { useAIChatContextUpdater } from '@/hooks'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'
import { useEmailBlasts } from '@/hooks/useEmailBlasts'
import { touchPointsApi } from '@/api/touchPoints'
import { useToast } from '@/hooks/useToast'
import { BlastStateBadge } from '@/components/webs/email-blasts/BlastStateBadge'
import type { EmailBlastViewDto } from '@/types/emailBlast'
import { assetApi, type AssetVersionSummaryDto } from '@/api/asset'
import { activationVersionState, type ActivationVersionState } from '@/utils/emailActivationVersion'

interface Props {
  clientCompanyId: string
  web: WebViewDto
  workspaceId: string
  campaignId: string
}

type ActivationFilter = 'All' | 'Email' | 'Phone' | 'Social'

const FILTER_OPTIONS: { key: ActivationFilter; label: string }[] = [
  { key: 'All', label: 'All' },
  { key: 'Email', label: 'Email' },
  { key: 'Phone', label: 'Phone' },
  { key: 'Social', label: 'Social' },
]

// Sort key for touchpoints in the flat list. Returns ms-since-epoch when
// a knowable fire time exists; otherwise pushes to the end. For Email
// Kind=Event, the anchor's absolute time is derived from the parent Web
// when the anchor is event-relative (OnlineEventStart / OnlineEventStartRegistrants / OnlineEventEnd).
// Per-recipient anchors (WebRegistered, AssetDownloaded, etc.) have no
// global time and sort to the end.
function sortableDate(tp: TouchPointViewDto, web: WebViewDto): number {
  if (tp.EmailProperties?.Rule) {
    const rule = tp.EmailProperties.Rule
    if (rule.Kind === 'Scheduled' && rule.SendAtAbsolute) {
      return parseServerTimestamp(rule.SendAtAbsolute) ?? Number.MAX_SAFE_INTEGER
    }
    if (rule.Kind === 'Event' && rule.EventType && rule.OffsetUnit && rule.OffsetValue != null) {
      const anchorMs = anchorTimeMs(rule.EventType, web)
      if (anchorMs !== null) {
        return anchorMs + offsetToMs(rule.OffsetValue, rule.OffsetUnit)
      }
    }
    return Number.MAX_SAFE_INTEGER
  }
  // Phone/Social ScheduledDate can serialize zone-less; pin to UTC so the sort key doesn't
  // shift by the viewer's offset (which silently reorders the list on non-UTC machines).
  const raw = tp.PhoneProperties?.ScheduledDate || tp.SocialProperties?.ScheduledDate || ''
  const t = parseServerTimestamp(raw) ?? NaN
  return Number.isFinite(t) ? t : Number.MAX_SAFE_INTEGER
}

// The API sends an OnlineEvent's Start/End as its OWN-zone wall-clock labeled +00:00 (for display),
// not a true instant. Interpret that wall-clock in the event's IANA zone to recover the real moment,
// so an event-relative send sorts against Scheduled sends (true instants) in the same frame. ORB-446.
function eventAnchorMs(wireValue: string | null | undefined, timeZone: string | null | undefined): number | null {
  if (!wireValue) return null
  // Strip the trailing zone designator (Z or ±hh:mm) but keep full precision, incl seconds — a
  // sanitize-to-minute-precision would floor a sub-minute anchor. The event-zone wall-clock reinterprets exactly.
  const wallClock = wireValue.replace(/(?:Z|[+-]\d{2}:?\d{2})$/i, '').trim()
  if (timeZone && wallClock) {
    try {
      return globalThis.Temporal.PlainDateTime.from(wallClock).toZonedDateTime(timeZone).epochMilliseconds
    } catch {
      // Unrecognized zone / unparseable wall-clock — fall back to the UTC-pinned value below.
    }
  }
  return parseServerTimestamp(wireValue)
}

function anchorTimeMs(eventType: EmailEventType, web: WebViewDto): number | null {
  switch (eventType) {
    // ORB-536. The registrant-scoped reminder shares OnlineEventStart's instant — it differs only in
    // audience — so it sorts to the same place on the timeline.
    case EmailEventType.OnlineEventStart:
    case EmailEventType.OnlineEventStartRegistrants:
      return eventAnchorMs(web.OnlineEvent?.StartDateTime, web.OnlineEvent?.TimeZone)
    case EmailEventType.OnlineEventEnd:
      return eventAnchorMs(web.OnlineEvent?.EndDateTime, web.OnlineEvent?.TimeZone)
    case EmailEventType.WebRegistered:
    case EmailEventType.AssetDownloaded:
    case EmailEventType.MeetingBooked:
    case EmailEventType.DemoBooked:
    case EmailEventType.OnlineEventAttended:
    case EmailEventType.OnlineEventMissed:
    case EmailEventType.EmailClicked:
      // Per-recipient anchors have no global time — caller sorts these to the end.
      return null
    default:
      return assertNever(eventType)
  }
}

function offsetToMs(value: number, unit: EmailOffsetUnit): number {
  switch (unit) {
    case EmailOffsetUnit.Minute: return value * 60_000
    case EmailOffsetUnit.Hour:   return value * 3_600_000
    case EmailOffsetUnit.Day:    return value * 86_400_000
    default: return assertNever(unit)
  }
}

function assertNever(x: never): never {
  throw new Error(`Unhandled EmailSendRule enum value: ${String(x)}`)
}

export function WebActivationsTab({ clientCompanyId, web, workspaceId, campaignId }: Props) {
  const { updateContext } = useAIChatContextUpdater()
  const { canEdit } = useWorkspaceCapabilities()

  // Use the shared touchpoint manager hook
  const {
    touchPoints,
    loading,
    error,
    saving,
    deleting,
    fetchTouchPoints,
    createTouchPoint,
    updateTouchPoint,
    deleteTouchPoint,
  } = useTouchPointManager({
    workspaceId,
    campaignId,
    webId: web.Id,
    typeFilters: ['Email', 'Phone', 'Social'],
    isCampaignLevel: false,
  })

  // Modal states (Phone / Social touchpoints stay in TouchPointModal)
  const [showModal, setShowModal] = useState(false)
  const [modalFixedType, setModalFixedType] = useState<string | undefined>(undefined)
  const [editingTouchPoint, setEditingTouchPoint] = useState<TouchPointViewDto | null>(null)

  // Email panel state (Email touchpoints use the slide-over editor)
  const [showEmailPanel, setShowEmailPanel] = useState(false)
  const [emailPanelTouchPoint, setEmailPanelTouchPoint] = useState<TouchPointViewDto | null>(null)

  // Blasts for this Web (ORB-378), correlated to their touchpoint so each activation knows whether a
  // send is staged/in-flight (locked) — one blast per touchpoint.
  const { showSuccess, showError } = useToast()

  // ORB-303: every saved version on this web, read once, to say which version each email activation sends and whether
  // that version may send. Read again with the activations, since signing a version off moves them.
  const [webVersions, setWebVersions] = useState<AssetVersionSummaryDto[] | null>(null)
  useEffect(() => {
    let cancelled = false
    void assetApi.getAssetVersionsByWeb(clientCompanyId, workspaceId, web.Id).then(response => {
      if (cancelled) return
      if (response.data) {
        setWebVersions(response.data)
      } else if (response.error) {
        showError('Could not load email versions', response.error)
      }
    })
    return () => { cancelled = true }
  }, [clientCompanyId, workspaceId, web.Id, touchPoints, showError])
  const versionStateOf = (tp: TouchPointViewDto): ActivationVersionState | null => {
    const emailProperties = tp.EmailProperties
    if (!webVersions || !emailProperties?.EmailAssetId) return null
    return activationVersionState(
      emailProperties.EmailAssetVersionNumber,
      webVersions.filter(version => version.AssetId === emailProperties.EmailAssetId))
  }

  const blastsHook = useEmailBlasts({ workspaceId, campaignId, webId: web.Id })
  const blastByTouchPoint = useMemo(() => {
    const map = new Map<string, EmailBlastViewDto>()
    for (const b of blastsHook.blasts ?? []) map.set(b.TouchPointId, b)
    return map
  }, [blastsHook.blasts])
  const [startingOverId, setStartingOverId] = useState<string | null>(null)
  const [reschedulingId, setReschedulingId] = useState<string | null>(null)

  // Cancel + clear a staged send so the activation can be edited and re-sent. The panel stays open on the
  // now-disarmed touchpoint; the server 409s if sending has already started.
  const handleStartOver = async () => {
    const tp = emailPanelTouchPoint
    if (!tp) return
    setStartingOverId(tp.Id)
    try {
      const res = await touchPointsApi.startOver(workspaceId, tp.Id)
      if (res.error || !res.data) {
        showError('Cannot start over', res.error || (res.status === 409
          ? 'This send may have already started — it can no longer be started over.'
          : 'Unable to start over.'))
        return
      }
      showSuccess('Activation reset', 'The staged send was cleared. Edit and reschedule to send it again.')
      setEmailPanelTouchPoint(res.data)
      await Promise.all([fetchTouchPoints(), blastsHook.load()])
    } finally {
      setStartingOverId(null)
    }
  }

  // Re-arm a started-over activation so the dispatch worker stages it again at its send time.
  const handleReschedule = async () => {
    const tp = emailPanelTouchPoint
    if (!tp) return
    setReschedulingId(tp.Id)
    try {
      const res = await touchPointsApi.reschedule(workspaceId, tp.Id)
      if (res.error || !res.data) {
        showError('Reschedule failed', res.error || 'Unable to reschedule this activation.')
        return
      }
      showSuccess('Activation rescheduled', 'It will send at its scheduled time.')
      setEmailPanelTouchPoint(res.data)
      await fetchTouchPoints()
    } finally {
      setReschedulingId(null)
    }
  }

  // Type-chooser menu for the New Activation button
  const [createMenuOpen, setCreateMenuOpen] = useState(false)
  const createMenuRef = useRef<HTMLDivElement>(null)

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletingTouchPoint, setDeletingTouchPoint] = useState<TouchPointViewDto | null>(null)

  // Activation filter chip strip (above the flat list)
  const [activeFilter, setActiveFilter] = useState<ActivationFilter>('All')

  // Precomputed counts for the filter chips
  const typeCounts = useMemo(() => {
    const counts: Record<ActivationFilter, number> = { All: touchPoints.length, Email: 0, Phone: 0, Social: 0 }
    for (const tp of touchPoints) {
      if (tp.Type === 'Email') counts.Email++
      else if (tp.Type === 'Phone') counts.Phone++
      else if (tp.Type === 'Social') counts.Social++
    }
    return counts
  }, [touchPoints])

  // Flat list, filtered + sorted chronologically using web-aware anchor times
  const filteredSorted = useMemo(() => {
    const subset = activeFilter === 'All'
      ? touchPoints
      : touchPoints.filter(tp => tp.Type === activeFilter)
    return [...subset].sort((a, b) => sortableDate(a, web) - sortableDate(b, web))
  }, [touchPoints, activeFilter, web])

  // Asset ids that other email touchpoints on this Web have bound. When editing
  // an existing touchpoint, exclude that touchpoint's own asset so the picker
  // still shows it in the "Unused" group (rather than the user's own pick
  // appearing as "Already used").
  const usedAssetIds = useMemo(() => {
    const ids = new Set<string>()
    for (const tp of touchPoints) {
      if (tp.Type !== 'Email') continue
      const aid = tp.EmailProperties?.EmailAssetId
      if (!aid) continue
      if (emailPanelTouchPoint && tp.Id === emailPanelTouchPoint.Id) continue
      ids.add(aid)
    }
    return ids
  }, [touchPoints, emailPanelTouchPoint])

  const lookups = useLookups()

  // Default IANA zone for a new Scheduled rule (ORB-352). The workspace TimeZone isn't carried in
  // the activations path, so seed from the viewer's browser zone — matching the repo's default-source
  // pattern for workspace creation. The user can override in the editor's timezone selector.
  const defaultTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || '', [])

  // Update AI context when viewing activations
  useEffect(() => {
    updateContext({
      campaignId,
      webId: web.Id,
      location: ChatLocations.TouchPointEditor,
      entityType: ChatEntityTypes.Web,
      entityId: web.Id,
      viewMode: ChatViewModes.View,
      availableFields: [],
      availableActions: ['Create Activation', 'View Activations'],
    })
  }, [workspaceId, campaignId, web.Id, updateContext])

  // Touchpoint types passed to the legacy modal — Meeting lives in its own tab,
  // Email now has its own slide-over panel.
  const touchPointTypes: LookupOptionDto[] = lookups.getTouchPointTypes()
    .filter(x => x.Key !== 'Meeting' && x.Key !== 'Email')
    .map(x => ({ Id: x.Key, DisplayName: x.Description }))

  const handleCreateEmail = () => {
    setCreateMenuOpen(false)
    setEmailPanelTouchPoint(null)
    setShowEmailPanel(true)
  }

  const handleCreateOther = (type: TouchPointType) => {
    setCreateMenuOpen(false)
    setEditingTouchPoint(null)
    setModalFixedType(type)
    setShowModal(true)
  }

  const handleEdit = (touchPoint: TouchPointViewDto) => {
    if (touchPoint.Type === TouchPointType.Email) {
      setEmailPanelTouchPoint(touchPoint)
      setShowEmailPanel(true)
      return
    }
    setEditingTouchPoint(touchPoint)
    setModalFixedType(touchPoint.Type)
    setShowModal(true)
  }

  // Close create-menu on outside click
  useEffect(() => {
    if (!createMenuOpen) return
    const onClick = (e: MouseEvent) => {
      if (createMenuRef.current && !createMenuRef.current.contains(e.target as Node)) {
        setCreateMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [createMenuOpen])

  const handleCloseModal = () => {
    if (saving) return

    setShowModal(false)
    setEditingTouchPoint(null)
    // Reset AI context back to View mode
    updateContext({
      campaignId,
      webId: web.Id,
      location: ChatLocations.TouchPointEditor,
      entityType: ChatEntityTypes.Web,
      entityId: web.Id,
      viewMode: ChatViewModes.View,
      availableFields: [],
      availableActions: ['Create Activation', 'View Activations'],
    })
  }

  const handleSave = async (formData: TouchPointFormData) => {
    if (editingTouchPoint) {
      // Update existing touchpoint
      const updateDto = buildTouchPointUpdateDto(formData, editingTouchPoint.ETag)
      const result = await updateTouchPoint(editingTouchPoint.Id, updateDto, 'Activation Updated')

      if (result.success) {
        setShowModal(false)
        setEditingTouchPoint(null)
      }
    } else {
      // Create new touchpoint
      const createDto = buildTouchPointCreateDto(formData, campaignId, web.Id)
      const result = await createTouchPoint(createDto, 'Activation Created')

      if (result.success) {
        setShowModal(false)
        setEditingTouchPoint(null)
      }
    }
  }

  const handleDeleteClick = (touchPoint: TouchPointViewDto) => {
    setDeletingTouchPoint(touchPoint)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deletingTouchPoint) return

    const result = await deleteTouchPoint(deletingTouchPoint.Id, deletingTouchPoint.Name, 'Activation Deleted')

    if (result.success) {
      setShowDeleteConfirm(false)
      setDeletingTouchPoint(null)
    }
  }

  const handleDeleteCancel = () => {
    if (!deleting) {
      setShowDeleteConfirm(false)
      setDeletingTouchPoint(null)
    }
  }

  const formatDateTime = (isoString: string) => {
    try {
      const date = new Date(isoString)
      // Use UTC methods since we store dates at UTC midnight
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      })
    } catch {
      return isoString
    }
  }

  return (
    <>
      <div>
        {/* Page header — no card chrome; whitespace is the boundary */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Activations</h2>
          {canEdit && (
            <div className="relative" ref={createMenuRef}>
              <button
                onClick={() => setCreateMenuOpen(v => !v)}
                disabled={loading}
                aria-haspopup="menu"
                aria-expanded={createMenuOpen}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-600 dark:hover:bg-primary-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>New Activation</span>
                <ChevronDown className="w-3.5 h-3.5 opacity-80" />
              </button>
              {createMenuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-1 w-52 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg z-20 py-1"
                >
                  <CreateMenuItem
                    onClick={handleCreateEmail}
                    icon={<Mail className="w-4 h-4" />}
                    title="Email activation"
                    subtitle="Asset + send rule"
                  />
                  <CreateMenuItem
                    onClick={() => handleCreateOther(TouchPointType.Phone)}
                    icon={<Phone className="w-4 h-4" />}
                    title="Phone touchpoint"
                    subtitle="Scheduled call"
                  />
                  <CreateMenuItem
                    onClick={() => handleCreateOther(TouchPointType.Social)}
                    icon={<MessageSquare className="w-4 h-4" />}
                    title="Social touchpoint"
                    subtitle="Scheduled post"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-800 dark:text-red-300">Failed to load activations</h3>
                <p className="text-sm text-red-700 dark:text-red-400 mt-1">{error}</p>
                <button
                  onClick={fetchTouchPoints}
                  className="mt-2 text-sm font-medium text-red-700 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 underline"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && touchPoints.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-slate-400 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-600 dark:text-slate-400">
              No activations yet. Create your first touchpoint to get started.
            </p>
          </div>
        )}

        {/* Activation filter chip strip */}
        {!loading && !error && touchPoints.length > 0 && (
          <div className="flex items-center gap-1 mb-3 text-sm">
            {FILTER_OPTIONS.map(f => (
              <button
                key={f.key}
                type="button"
                onClick={() => setActiveFilter(f.key)}
                aria-pressed={activeFilter === f.key}
                className={
                  'px-2.5 py-1 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ' +
                  (activeFilter === f.key
                    ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-medium'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100')
                }
              >
                <span>{f.label}</span>
                <span className="ml-1.5 text-xs text-slate-400 dark:text-slate-500">{typeCounts[f.key]}</span>
              </button>
            ))}
          </div>
        )}

        {/* Flat chronological activation list */}
        {!loading && !error && touchPoints.length > 0 && (
          <div className="-mx-3 divide-y divide-slate-100 dark:divide-slate-800">
            {filteredSorted.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                No {activeFilter.toLowerCase()} activations yet.
              </div>
            ) : (
              filteredSorted.map(tp => (
                <ActivationRow
                  key={tp.Id}
                  tp={tp}
                  blast={blastByTouchPoint.get(tp.Id) ?? null}
                  versionState={versionStateOf(tp)}
                  formatDateTime={formatDateTime}
                  onEdit={() => handleEdit(tp)}
                  onDelete={() => handleDeleteClick(tp)}
                  canEdit={canEdit}
                />
              ))
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Modal — Phone / Social */}
      {showModal && (
        <TouchPointModal
          touchPoint={editingTouchPoint}
          touchPointTypes={touchPointTypes}
          fixedType={modalFixedType}
          onClose={handleCloseModal}
          onSave={handleSave}
          saving={saving}
          clientCompanyId={clientCompanyId}
          campaignId={campaignId}
          webId={web.Id}
        />
      )}

      {/* Email activation slide-over panel */}
      <EmailActivationPanel
        isOpen={showEmailPanel}
        touchPoint={emailPanelTouchPoint}
        clientCompanyId={clientCompanyId}
        workspaceId={workspaceId}
        campaignId={campaignId}
        webId={web.Id}
        usedAssetIds={usedAssetIds}
        includedFileImports={web.IncludedFileImports}
        defaultTimeZone={defaultTimeZone}
        blast={emailPanelTouchPoint ? (blastByTouchPoint.get(emailPanelTouchPoint.Id) ?? null) : null}
        onStartOver={handleStartOver}
        onReschedule={handleReschedule}
        startingOver={!!emailPanelTouchPoint && startingOverId === emailPanelTouchPoint.Id}
        rescheduling={!!emailPanelTouchPoint && reschedulingId === emailPanelTouchPoint.Id}
        onClose={() => {
          setShowEmailPanel(false)
          setEmailPanelTouchPoint(null)
        }}
        onSaved={() => {
          fetchTouchPoints()
        }}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Delete Activation"
        message={`Are you sure you want to delete "${deletingTouchPoint?.Name}"? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={deleting}
      />
    </>
  )
}

/**
 * The version an email activation sends (ORB-303), and what stops or waits on it: a version that isn't signed off
 * sends nothing, and a newer one waiting for sign-off changes nothing until it is signed off.
 */
function ActivationVersionChips({ state }: { state: ActivationVersionState }) {
  const chip = 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap '
  if (!state.signedOffAt) {
    return (
      <span className={chip + 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}>
        {state.versionNumber === 0 ? "Won't send · no email version" : `Won't send · v${state.versionNumber} not signed off`}
      </span>
    )
  }
  return (
    <>
      <span className={chip + 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'} title="The version of its email this activation sends">
        v{state.versionNumber}
      </span>
      {state.newerVersionNumber && (
        <span className={chip + 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'}>
          v{state.newerVersionNumber} waiting for sign-off
        </span>
      )}
    </>
  )
}

interface ActivationRowProps {
  tp: TouchPointViewDto
  blast: EmailBlastViewDto | null
  versionState: ActivationVersionState | null
  formatDateTime: (iso: string) => string
  onEdit: () => void
  onDelete: () => void
  canEdit: boolean
}

function ActivationRow({ tp, blast, versionState, formatDateTime, onEdit, onDelete, canEdit }: ActivationRowProps) {
  const primary = primarySchedule(tp, formatDateTime)
  const secondary = secondaryDetail(tp)

  const details = (
    <>
      <TypeGlyph type={tp.Type} />
      <div className="flex-1 min-w-0 flex items-baseline gap-3 flex-wrap">
        <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
          {tp.Name}
        </span>
        <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
          {primary}
        </span>
      </div>
      {versionState && <ActivationVersionChips state={versionState} />}
      {tp.Type === 'Email' && blast && <BlastStateBadge state={blast.State} />}
      {tp.Type === 'Email' && !blast && tp.EmailProperties?.Armed === false && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300 whitespace-nowrap">
          Not scheduled
        </span>
      )}
      {secondary && (
        <span className="hidden sm:inline text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
          {secondary}
        </span>
      )}
    </>
  )

  // Read-only variant: no interactivity, no edit/delete affordances.
  if (!canEdit) {
    return <div className="group flex items-center gap-3 px-3 py-2.5">{details}</div>
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onEdit()
        }
      }}
      className="group flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset transition-colors"
    >
      {details}
      <div className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 flex items-center gap-1 transition-opacity flex-shrink-0">
        <button
          type="button"
          onClick={(e: MouseEvent) => { e.stopPropagation(); onEdit() }}
          className="p-1.5 text-slate-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          aria-label="Edit activation"
          title="Edit"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={(e: MouseEvent) => { e.stopPropagation(); onDelete() }}
          className="p-1.5 text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
          aria-label="Delete activation"
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  )
}

function TypeGlyph({ type }: { type: string }) {
  const Icon = type === 'Email' ? Mail : type === 'Phone' ? Phone : type === 'Social' ? MessageSquare : Calendar
  return (
    <div
      className="w-7 h-7 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 flex items-center justify-center flex-shrink-0"
      aria-label={type}
    >
      <Icon className="w-3.5 h-3.5" />
    </div>
  )
}

function primarySchedule(tp: TouchPointViewDto, formatDateTime: (iso: string) => string): string {
  if (tp.Type === 'Email' && tp.EmailProperties?.Rule) {
    return formatEmailRule(tp.EmailProperties.Rule)
  }
  if (tp.Type === 'Phone' && tp.PhoneProperties?.ScheduledDate) {
    return formatDateTime(tp.PhoneProperties.ScheduledDate)
  }
  if (tp.Type === 'Social' && tp.SocialProperties?.ScheduledDate) {
    return formatDateTime(tp.SocialProperties.ScheduledDate)
  }
  return '—'
}

function secondaryDetail(tp: TouchPointViewDto): string | null {
  if (tp.Type === 'Phone' && tp.PhoneProperties) {
    const n = tp.PhoneProperties.NumberOfDials
    return `${n} ${n === 1 ? 'dial' : 'dials'}`
  }
  if (tp.Type === 'Social' && tp.SocialProperties?.Platform) {
    return tp.SocialProperties.Platform
  }
  return null
}

interface CreateMenuItemProps {
  icon: preact.ComponentChildren
  title: string
  subtitle: string
  onClick: () => void
}

function CreateMenuItem({ icon, title, subtitle, onClick }: CreateMenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="w-full flex items-start gap-3 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700 focus:bg-slate-50 dark:focus:bg-slate-700 focus:outline-none transition-colors"
    >
      <span className="mt-0.5 text-slate-500 dark:text-slate-400">{icon}</span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">{title}</span>
        <span className="block text-xs text-slate-500 dark:text-slate-400">{subtitle}</span>
      </span>
    </button>
  )
}
